import { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  User,
  Lock,
  Bell,
  Trash2,
  Upload,
  X,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  CreditCard,
  Key,
  Users,
  BarChart3,
  LogOut,
  ChevronRight,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';

// Lazy load sub-pages for better performance
const BillingContent = lazy(() => import('./settings/BillingContent'));
const APIContent = lazy(() => import('./settings/APIContent'));
const TeamContent = lazy(() => import('./settings/TeamContent'));
const AnalyticsContent = lazy(() => import('./settings/AnalyticsContent'));

const API_URL = import.meta.env.VITE_API_URL || '/api';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

type SettingsTab = 'profile' | 'billing' | 'api' | 'team' | 'analytics';

interface NavItem {
  id: SettingsTab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  comingSoon?: boolean;
}

const settingsNavigation: NavItem[] = [
  { id: 'profile', label: 'Profile', icon: User, description: 'Personal information & security' },
  { id: 'billing', label: 'Billing', icon: CreditCard, description: 'Subscription & payments' },
  { id: 'api', label: 'API', icon: Key, description: 'API keys & webhooks', comingSoon: true },
  { id: 'team', label: 'Team', icon: Users, description: 'Manage team members' },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, description: 'Usage & performance' },
];

// Loading component for lazy-loaded content
const ContentLoader = () => {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <RefreshCw className={`w-8 h-8 animate-spin ${isDark ? 'text-accent-purple' : 'text-purple-600'}`} />
    </div>
  );
};

export default function Settings() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, updateUser, logout } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  // Check if redirected from successful payment or tab parameter
  const isPaymentSuccess = searchParams.get('success') === 'true';
  const tabParam = searchParams.get('tab') as SettingsTab | null;
  const initialTab = tabParam && ['profile', 'billing', 'api', 'team', 'analytics'].includes(tabParam)
    ? tabParam
    : isPaymentSuccess ? 'billing' : 'profile';
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab);

  // Profile state
  const [name, setName] = useState(user?.name || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);

  // Notification preferences state
  const [notifyTaskCompletions, setNotifyTaskCompletions] = useState(true);
  const [notifyWeeklyDigest, setNotifyWeeklyDigest] = useState(true);
  const [notifyTipsUpdates, setNotifyTipsUpdates] = useState(true);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [savingNotifications, setSavingNotifications] = useState(false);

  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Sync state with user when user changes
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setAvatarUrl(user.avatar_url || '');
    }
  }, [user]);

  // Clear message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Load notification preferences
  useEffect(() => {
    const loadNotificationPreferences = async () => {
      if (!token) return;

      try {
        const response = await fetch(`${API_URL}/user/notifications`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json() as {
          success: boolean;
          data?: { task_completions: number; weekly_digest: number; tips_updates: number };
        };

        if (data.success && data.data) {
          setNotifyTaskCompletions(Boolean(data.data.task_completions));
          setNotifyWeeklyDigest(Boolean(data.data.weekly_digest));
          setNotifyTipsUpdates(Boolean(data.data.tips_updates));
        }
      } catch (error) {
        console.error('Failed to load notification preferences:', error);
      } finally {
        setLoadingNotifications(false);
      }
    };

    loadNotificationPreferences();
  }, [token]);

  // Save notification preferences
  const saveNotificationPreference = async (key: string, value: boolean) => {
    if (!token) return;

    setSavingNotifications(true);
    try {
      const response = await fetch(`${API_URL}/user/notifications`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          [key]: value,
        }),
      });

      const data = await response.json() as { success: boolean; error?: string };

      if (data.success) {
        setMessage({ type: 'success', text: 'Notification preference updated' });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update preference' });
      }
    } catch (error) {
      console.error('Failed to save notification preference:', error);
      setMessage({ type: 'error', text: 'Failed to update preference' });
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleNotificationToggle = (key: string, currentValue: boolean, setter: (value: boolean) => void) => {
    const newValue = !currentValue;
    setter(newValue);
    saveNotificationPreference(key, newValue);
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please upload an image file' });
      return;
    }

    // Validate file size (500KB)
    if (file.size > 500 * 1024) {
      setMessage({ type: 'error', text: 'Image size must be less than 500KB' });
      return;
    }

    setUploadingAvatar(true);

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      setAvatarPreview(base64);
      setUploadingAvatar(false);
    };
    reader.onerror = () => {
      setMessage({ type: 'error', text: 'Failed to read image file' });
      setUploadingAvatar(false);
    };
    reader.readAsDataURL(file);

    // Reset input
    e.target.value = '';
  };

  const removeAvatar = () => {
    setAvatarPreview(null);
    setAvatarUrl('');
  };

  const saveProfile = async () => {
    if (!token) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/user/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: name.trim(),
          avatar_url: avatarPreview || avatarUrl || null,
        }),
      });

      const data = await response.json() as { success: boolean; data?: { name: string; avatar_url: string }; error?: string };

      if (data.success && data.data) {
        // Update local auth store
        updateUser({
          name: data.data.name,
          avatar_url: data.data.avatar_url,
        });
        setAvatarUrl(data.data.avatar_url || '');
        setAvatarPreview(null);
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update profile' });
      }
    } catch (error) {
      console.error('Failed to save profile:', error);
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const updatePassword = async () => {
    if (!token) return;

    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: 'Please fill in all password fields' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'New password must be at least 8 characters' });
      return;
    }

    setUpdatingPassword(true);
    try {
      const response = await fetch(`${API_URL}/user/password`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      const data = await response.json() as { success: boolean; error?: string };

      if (data.success) {
        setMessage({ type: 'success', text: 'Password updated successfully!' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update password' });
      }
    } catch (error) {
      console.error('Failed to update password:', error);
      setMessage({ type: 'error', text: 'Failed to update password. Please try again.' });
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const displayAvatar = avatarPreview || avatarUrl;

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:items-start">
      {/* Settings Navigation Sidebar - Sticky on desktop */}
      <motion.aside
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className={`lg:w-72 flex-shrink-0 rounded-2xl border p-4 lg:sticky lg:top-20 ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}
      >
        <h2 className={`text-lg font-semibold mb-4 px-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>Settings</h2>

        <nav className="space-y-1">
          {settingsNavigation.map((item) => {
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center justify-between px-3 py-3 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-accent text-white shadow-glow-sm'
                    : isDark
                      ? 'text-text-secondary hover:bg-dark-700 hover:text-white'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <item.icon className="w-5 h-5" />
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{item.label}</span>
                      {item.comingSoon && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          isActive
                            ? 'bg-white/20 text-white'
                            : 'bg-amber-500/20 text-amber-500'
                        }`}>
                          Soon
                        </span>
                      )}
                    </div>
                    <span className={`text-xs ${isActive ? 'text-white/70' : isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                      {item.description}
                    </span>
                  </div>
                </div>
                <ChevronRight className={`w-4 h-4 ${isActive ? 'text-white/70' : 'opacity-0'}`} />
              </button>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className={`mt-6 pt-4 border-t ${isDark ? 'border-border' : 'border-slate-200'}`}>
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-3 py-3 text-accent-red hover:bg-accent-red/10 rounded-xl transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Log out</span>
          </button>
        </div>
      </motion.aside>

      {/* Main Content Area */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
        className="flex-1 min-w-0"
      >
        {/* Message Toast */}
        <AnimatePresence>
          {message && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`fixed top-4 right-4 z-50 p-4 rounded-xl flex items-center gap-3 shadow-lg ${
                message.type === 'success'
                  ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                  : 'bg-red-500/10 border border-red-500/20 text-red-400'
              }`}
            >
              {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              <span>{message.text}</span>
              <button onClick={() => setMessage(null)} className="ml-2">
                <X className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hidden file input */}
        <input
          ref={avatarInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          className="hidden"
          onChange={handleAvatarChange}
        />

        {/* Profile Tab */}
        <AnimatePresence mode="wait">
          {activeTab === 'profile' && (
            <motion.div
              key="profile"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              {/* Header */}
              <div>
                <h1 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Profile Settings</h1>
                <p className={isDark ? 'text-text-secondary' : 'text-slate-500'}>Manage your personal information and security</p>
              </div>

              {/* Profile Information */}
              <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
                <h3 className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>Profile Information</h3>

                {/* Avatar */}
                <div className="flex items-center space-x-4 mb-6">
                  <div className="relative">
                    {displayAvatar ? (
                      <div className="relative">
                        <img
                          src={displayAvatar}
                          alt="Avatar"
                          className="w-20 h-20 rounded-full object-cover"
                        />
                        <button
                          onClick={removeAvatar}
                          className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gradient-accent flex items-center justify-center">
                        <span className="text-2xl font-bold text-white">
                          {user?.name?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                    )}
                    {uploadingAvatar && (
                      <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                        <RefreshCw className="w-6 h-6 text-white animate-spin" />
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    Change Avatar
                  </button>
                </div>

                {/* Form */}
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="input-field"
                      placeholder="Enter your name"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                      Email
                    </label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      className="input-field"
                      disabled
                    />
                    <p className={`text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                      Email cannot be changed
                    </p>
                  </div>
                </div>

                <button
                  onClick={saveProfile}
                  disabled={saving}
                  className="btn-primary mt-6 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>

              {/* Security Section */}
              <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
                <div className="flex items-center gap-3 mb-6">
                  <Lock className={`w-5 h-5 ${isDark ? 'text-accent-purple' : 'text-purple-600'}`} />
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Change Password</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="input-field"
                      placeholder="Enter current password"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                      New Password
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="input-field"
                      placeholder="Enter new password"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="input-field"
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>
                <button
                  onClick={updatePassword}
                  disabled={updatingPassword}
                  className="btn-primary mt-6 flex items-center gap-2"
                >
                  {updatingPassword ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </div>

              {/* Notifications Section */}
              <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
                <div className="flex items-center gap-3 mb-6">
                  <Bell className={`w-5 h-5 ${isDark ? 'text-accent-cyan' : 'text-cyan-600'}`} />
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Email Notifications</h3>
                </div>
                {loadingNotifications ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-accent-purple" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Task completions */}
                    <div className={`flex items-center justify-between py-3 border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Task completions</p>
                        <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Get notified when tasks finish</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={notifyTaskCompletions}
                          onChange={() => handleNotificationToggle('task_completions', notifyTaskCompletions, setNotifyTaskCompletions)}
                          disabled={savingNotifications}
                        />
                        <div className={`w-11 h-6 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-purple ${isDark ? 'bg-dark-700' : 'bg-slate-300'} ${savingNotifications ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                      </label>
                    </div>

                    {/* Weekly digest */}
                    <div className={`flex items-center justify-between py-3 border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Weekly digest</p>
                        <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Summary of your AI activity</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={notifyWeeklyDigest}
                          onChange={() => handleNotificationToggle('weekly_digest', notifyWeeklyDigest, setNotifyWeeklyDigest)}
                          disabled={savingNotifications}
                        />
                        <div className={`w-11 h-6 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-purple ${isDark ? 'bg-dark-700' : 'bg-slate-300'} ${savingNotifications ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                      </label>
                    </div>

                    {/* Tips & updates */}
                    <div className={`flex items-center justify-between py-3 ${isDark ? 'border-border' : 'border-slate-200'}`}>
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Tips & updates</p>
                        <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Product news and tips</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={notifyTipsUpdates}
                          onChange={() => handleNotificationToggle('tips_updates', notifyTipsUpdates, setNotifyTipsUpdates)}
                          disabled={savingNotifications}
                        />
                        <div className={`w-11 h-6 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-purple ${isDark ? 'bg-dark-700' : 'bg-slate-300'} ${savingNotifications ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Danger Zone */}
              <div className={`rounded-2xl p-6 border border-accent-red/30 ${isDark ? 'bg-dark-800' : 'bg-white shadow-sm'}`}>
                <h3 className="text-lg font-semibold text-accent-red mb-4">Danger Zone</h3>
                <p className={`text-sm mb-4 ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
                  Once you delete your account, there is no going back. Please be certain.
                </p>
                <button className="flex items-center space-x-2 px-4 py-2 bg-accent-red/10 text-accent-red rounded-xl hover:bg-accent-red/20 transition-colors">
                  <Trash2 className="w-4 h-4" />
                  <span>Delete Account</span>
                </button>
              </div>
            </motion.div>
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <motion.div
              key="billing"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <Suspense fallback={<ContentLoader />}>
                <BillingContent />
              </Suspense>
            </motion.div>
          )}

          {/* API Tab - Coming Soon */}
          {activeTab === 'api' && (
            <motion.div
              key="api"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              {/* Header */}
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>API & Developer Tools</h1>
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-500">
                    Coming Soon
                  </span>
                </div>
                <p className={isDark ? 'text-text-secondary' : 'text-slate-500'}>
                  Manage API keys, webhooks, and integrations
                </p>
              </div>

              {/* Coming Soon Card */}
              <div className={`rounded-2xl p-8 border text-center ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mx-auto mb-4">
                  <Key className={`w-8 h-8 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  API Access Coming Soon
                </h3>
                <p className={`max-w-md mx-auto mb-6 ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
                  We're working on providing API access so you can integrate SweetPilot with your own applications.
                  Stay tuned for updates!
                </p>
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl ${isDark ? 'bg-dark-700 text-text-secondary' : 'bg-slate-100 text-slate-600'}`}>
                  <Bell className="w-4 h-4" />
                  <span className="text-sm">We'll notify you when it's ready</span>
                </div>
              </div>

              {/* Features Preview */}
              <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
                <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  What to Expect
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { title: 'API Keys', description: 'Generate and manage API keys for programmatic access' },
                    { title: 'Webhooks', description: 'Receive real-time notifications for events' },
                    { title: 'Documentation', description: 'Comprehensive API docs and examples' },
                    { title: 'Rate Limits', description: 'Flexible rate limits based on your plan' },
                  ].map((feature, index) => (
                    <div key={index} className={`p-4 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-slate-50'}`}>
                      <h4 className={`font-medium mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{feature.title}</h4>
                      <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Team Tab */}
          {activeTab === 'team' && (
            <motion.div
              key="team"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <Suspense fallback={<ContentLoader />}>
                <TeamContent />
              </Suspense>
            </motion.div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <motion.div
              key="analytics"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <Suspense fallback={<ContentLoader />}>
                <AnalyticsContent />
              </Suspense>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
